import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InsightsIcon from '@mui/icons-material/Insights';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Tooltip from '@mui/material/Tooltip';

import SftGlobalData from '../library/SftGlobalData';
import Colors from '../library/Colors';
import ReportRunModal from '../components/reports/ReportRunModal';
import WebReportBuilderModal from '../components/reports/WebReportBuilderModal';
import RevenueMapModal from '../components/reports/RevenueMapModal';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import '../App.css';
import '../styles/main.css';

const localizedTitle = (report, lang) => {
    if (!report) return '';
    switch (lang) {
        case 'nl': return report.reptitle  || report.reptitle2 || report.reptitle3 || report.reptitle4 || '';
        case 'fr': return report.reptitle2 || report.reptitle  || report.reptitle3 || report.reptitle4 || '';
        case 'en': return report.reptitle3 || report.reptitle  || report.reptitle2 || report.reptitle4 || '';
        default:   return report.reptitle  || report.reptitle2 || report.reptitle3 || report.reptitle4 || '';
    }
};

const matchSearch = (report, term, lang) => {
    if (!term) return true;
    const needle = term.toLowerCase();
    const haystack = [
        localizedTitle(report, lang),
        report.reptitle,
        report.reptitle2,
        report.reptitle3,
        report.reptitle4,
        report.repmodule,
        String(report.repid),
        String(report.repreleaseid ?? ''),
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(needle);
};

const ReportsScreen = (props) => {
    const [loading, setLoading] = useState(true);
    const [softtouch, setSofttouch] = useState([]);
    const [custom, setCustom] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedReport, setSelectedReport] = useState(null);
    // Shared sort across both lists. null = backend default order.
    const [sortField, setSortField] = useState(null); // 'repid' | 'repmodule' | 'repreleaseid' | 'description' | null
    const [sortDir, setSortDir]     = useState(null); // 'asc' | 'desc' | null

    // Native web-report builder.
    const [savedWebReports, setSavedWebReports] = useState([]);
    const [builderOpen, setBuilderOpen] = useState(false);
    const [builderInit, setBuilderInit] = useState(null); // {dataset, config, id, name} | null

    // Built-in "Omzetkaart" revenue map (always present, not stored in web_report).
    const [mapOpen, setMapOpen] = useState(false);

    const isDark = props.theme === 'dark';
    const accentColor = isDark ? Colors.dark_text : Colors.light_text;

    const loadSavedWebReports = () => {
        SftGlobalData.post('webreports&action=listSaved', { key: props.api }).then(response => {
            if (typeof response.error === 'undefined' && Array.isArray(response?.data?.reports)) {
                setSavedWebReports(response.data.reports);
            }
        });
    };

    useEffect(() => {
        props.changeScreen && props.changeScreen('reports');
        SftGlobalData.post('reports&action=fetchReports', { key: props.api }).then(response => {
            if (typeof response.error === 'undefined' && response?.data) {
                setSofttouch(Array.isArray(response.data.softtouch) ? response.data.softtouch : []);
                setCustom(Array.isArray(response.data.custom) ? response.data.custom : []);
            } else {
                props.notify(props.t('reports_fetch_error') || props.t('errorOccured'), 7500, 'top-center', props.theme, 1);
            }
            setLoading(false);
        });
        loadSavedWebReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.api]);

    const openNewBuilder = () => { setBuilderInit(null); setBuilderOpen(true); };
    const openSavedBuilder = (rep) => {
        setBuilderInit({ dataset: rep.dataset, config: rep.config || {}, id: rep.id, name: rep.name });
        setBuilderOpen(true);
    };
    const deleteSavedWebReport = (rep, e) => {
        e.stopPropagation();
        SftGlobalData.post('webreports&action=deleteDefinition', { key: props.api, id: rep.id }).then(() => {
            loadSavedWebReports();
        });
    };

    const sortValueFor = (report, field, lang) => {
        switch (field) {
            case 'repid':         return Number(report.repid) || 0;
            case 'repreleaseid':  return Number(report.repreleaseid) || 0;
            case 'repmodule':     return String(report.repmodule || '').toLowerCase();
            case 'description':   return String(localizedTitle(report, lang) || '').toLowerCase();
            default:              return null;
        }
    };

    const applySort = (rows) => {
        if (!sortField || !sortDir) return rows;
        const copy = [...rows];
        copy.sort((a, b) => {
            const av = sortValueFor(a, sortField, props.lang);
            const bv = sortValueFor(b, sortField, props.lang);
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ?  1 : -1;
            return 0;
        });
        return copy;
    };

    const filteredSofttouch = useMemo(
        () => applySort(softtouch.filter(r => matchSearch(r, searchTerm, props.lang))),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [softtouch, searchTerm, props.lang, sortField, sortDir]
    );
    const filteredCustom = useMemo(
        () => applySort(custom.filter(r => matchSearch(r, searchTerm, props.lang))),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [custom, searchTerm, props.lang, sortField, sortDir]
    );

    // Click header: cycle null → asc → desc → null. Switching to a new column starts at asc.
    const toggleSort = (field) => {
        if (sortField !== field) {
            setSortField(field);
            setSortDir('asc');
        } else if (sortDir === 'asc') {
            setSortDir('desc');
        } else {
            setSortField(null);
            setSortDir(null);
        }
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return null;
        return sortDir === 'asc'
            ? <ArrowUpwardIcon fontSize="inherit" sx={{ fontSize: '0.95rem', ml: 0.5 }} />
            : <ArrowDownwardIcon fontSize="inherit" sx={{ fontSize: '0.95rem', ml: 0.5 }} />;
    };

    const headerCellSx = (field) => ({
        cursor: field ? 'pointer' : 'default',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        '&:hover': field ? { color: 'text.primary' } : undefined,
    });

    const rowHeight = 36;
    const gridTemplate = '32px 70px 110px 110px minmax(0, 1fr) auto';

    const renderHeader = () => (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplate,
                columnGap: 1.5,
                alignItems: 'center',
                height: rowHeight,
                px: 2,
                fontWeight: 600,
                fontSize: '0.78rem',
                textTransform: 'uppercase',
                color: 'text.secondary',
                letterSpacing: 0.4,
                backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            }}
        >
            <Box />
            <Box sx={headerCellSx('repid')}        onClick={() => toggleSort('repid')}>
                {props.t('id') || 'ID'}<SortIcon field="repid" />
            </Box>
            <Box sx={headerCellSx('repmodule')}    onClick={() => toggleSort('repmodule')}>
                {props.t('module') || 'Module'}<SortIcon field="repmodule" />
            </Box>
            <Box sx={headerCellSx('repreleaseid')} onClick={() => toggleSort('repreleaseid')}>
                {props.t('reports_repid') || 'Report ID'}<SortIcon field="repreleaseid" />
            </Box>
            <Box sx={headerCellSx('description')}  onClick={() => toggleSort('description')}>
                {props.t('reports_description') || 'Description'}<SortIcon field="description" />
            </Box>
            <Box />
        </Box>
    );

    const rowDividerColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

    const renderRow = (report, idx, isLast) => (
        <ListItem
            key={report.repid}
            disablePadding
            sx={{
                height: rowHeight,
                borderBottom: isLast ? 'none' : `1px solid ${rowDividerColor}`,
            }}
        >
            <ListItemButton
                onClick={() => setSelectedReport(report)}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    columnGap: 1.5,
                    alignItems: 'center',
                    height: rowHeight,
                    minHeight: rowHeight,
                    px: 2,
                    py: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {report.is_layout_only && (
                        <Tooltip title={props.t('reports_layoutOnlyWarning') || 'This report uses a layout file and might not be shown fully correctly.'}>
                            <ArticleOutlinedIcon fontSize="small" sx={{ color: Colors.sft_blue }} />
                        </Tooltip>
                    )}
                </Box>
                <Typography variant="body2" noWrap color="text.secondary">{report.repid}</Typography>
                <Typography variant="body2" noWrap>{report.repmodule || '—'}</Typography>
                <Typography variant="body2" noWrap>{report.repreleaseid ?? '—'}</Typography>
                <Typography variant="body2" noWrap title={localizedTitle(report, props.lang)}>
                    {localizedTitle(report, props.lang)}
                </Typography>
                {report.has_variables ? (
                    <Chip
                        size="small"
                        icon={<TuneIcon />}
                        label={report.has_required_var
                            ? (props.t('reports_variables_required') || 'Required')
                            : (props.t('reports_variables_optional') || 'Variables')
                        }
                        color={report.has_required_var ? 'warning' : 'default'}
                        variant="outlined"
                        sx={{ height: 24 }}
                    />
                ) : <span />}
            </ListItemButton>
        </ListItem>
    );

    const renderList = (title, items) => (
        <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }} className={props.theme}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', p: 0, '&:last-child': { pb: 0 } }}>
                <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" sx={{ color: accentColor }}>{title}</Typography>
                    <Chip label={items.length} size="small" />
                </Box>
                <Divider />
                {items.length > 0 && renderHeader()}
                <Box sx={{ maxHeight: 480, overflowY: 'auto' }}>
                    {items.length === 0 && (
                        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="body2">{props.t('reports_noResults') || 'No reports'}</Typography>
                        </Box>
                    )}
                    {items.length > 0 && (
                        <List dense disablePadding>
                            {items.map((report, idx) => renderRow(report, idx, idx === items.length - 1))}
                        </List>
                    )}
                </Box>
            </CardContent>
        </Card>
    );

    const outerClass = (props.screenWidth < 600 ? 'not_expanded' : 'expanded')
        + ' container_content _80perc '
        + props.theme + ' container_content_' + props.theme;

    return (
        <div className={outerClass} style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Typography variant="h5" sx={{ color: accentColor }}>{props.t('reports_title') || 'Reports'}</Typography>
            </Stack>

            <TextField
                size="small"
                fullWidth
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={props.t('reports_search') || 'Search reports…'}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
                className={props.theme}
            />

            {/* Native web-report builder */}
            <Card sx={{ width: '100%' }} className={props.theme}>
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                            <InsightsIcon sx={{ color: Colors.sft_blue }} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h6" sx={{ color: accentColor, lineHeight: 1.2 }}>
                                    {props.t('webreport_title') || 'Report builder'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {props.t('webreport_subtitle') || 'Build a custom report over sales, stock, customers and suppliers.'}
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openNewBuilder}
                            sx={{ backgroundColor: Colors.sft_blue, '&:hover': { backgroundColor: Colors.sft_blue_darker || Colors.sft_blue }, whiteSpace: 'nowrap' }}
                        >
                            {props.t('webreport_new') || 'New web report'}
                        </Button>
                    </Box>
                    {(
                        <>
                            <Divider />
                            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                                <Typography variant="overline" color="text.secondary">
                                    {props.t('webreport_savedHeading') || 'Saved web reports'}
                                </Typography>
                            </Box>
                            <List dense disablePadding sx={{ pb: 1 }}>
                                {/* Built-in revenue map — always first, not deletable */}
                                <ListItem disablePadding>
                                    <ListItemButton onClick={() => setMapOpen(true)} sx={{ px: 2 }}>
                                        <MapOutlinedIcon fontSize="small" sx={{ mr: 1.5, color: Colors.sft_blue }} />
                                        <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                                            {props.t('webreport_map_title') || 'Revenue map (Belgium)'}
                                        </Typography>
                                        <Chip size="small" variant="outlined" label={props.t('webreport_map_badge') || 'Map'} sx={{ mr: 4 }} />
                                    </ListItemButton>
                                </ListItem>
                                {savedWebReports.map(rep => (
                                    <ListItem
                                        key={rep.id}
                                        disablePadding
                                        secondaryAction={
                                            <Tooltip title={props.t('webreport_delete') || 'Delete'}>
                                                <IconButton edge="end" size="small" onClick={(e) => deleteSavedWebReport(rep, e)}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    >
                                        <ListItemButton onClick={() => openSavedBuilder(rep)} sx={{ px: 2 }}>
                                            <InsightsIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                                            <Typography variant="body2" sx={{ flex: 1 }} noWrap>{rep.name}</Typography>
                                            <Chip size="small" variant="outlined" label={rep.dataset} sx={{ mr: 4 }} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </CardContent>
            </Card>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && (
                <Stack direction="column" spacing={2}>
                    {renderList(props.t('reports_customHeading')    || 'Custom reports',    filteredCustom)}
                    {renderList(props.t('reports_softtouchHeading') || 'SoftTouch reports', filteredSofttouch)}
                </Stack>
            )}

            <ReportRunModal
                open={selectedReport !== null}
                report={selectedReport}
                onClose={() => setSelectedReport(null)}
                api={props.api}
                sftUserId={props.sftUserId}
                t={props.t}
                lang={props.lang}
                theme={props.theme}
                screenWidth={props.screenWidth}
                notify={props.notify}
            />

            <WebReportBuilderModal
                open={builderOpen}
                onClose={() => setBuilderOpen(false)}
                onSaved={loadSavedWebReports}
                initialDataset={builderInit?.dataset}
                initialConfig={builderInit?.config}
                initialSavedId={builderInit?.id}
                initialName={builderInit?.name}
                api={props.api}
                sftUserId={props.sftUserId}
                t={props.t}
                lang={props.lang}
                theme={props.theme}
                screenWidth={props.screenWidth}
                notify={props.notify}
            />

            <RevenueMapModal
                open={mapOpen}
                onClose={() => setMapOpen(false)}
                api={props.api}
                sftUserId={props.sftUserId}
                t={props.t}
                lang={props.lang}
                theme={props.theme}
                screenWidth={props.screenWidth}
                notify={props.notify}
            />
        </div>
    );
};

export default ReportsScreen;
